import { CurrentCardService } from '@addins/core/core';
import { ViewLoaderService } from '@addins/core/menus';
import { Injectable, Type } from '@angular/core';
import { IonNav } from '@ionic/angular';
import { CallCard } from '@models/imported/SagaSchema/CallCard';
import { TranslateService } from '@ngx-translate/core';
import { NavService } from '@services/navigation/navigation.service';
import { ProfileService } from '@services/profile/profile.service';
import { Tool, ToolsProvider, ToolsProviderService } from '@services/tools';
import { AddinExecute, AddinService, Execute } from '@techwan/ionic-core';
import { filter, first, Subscription } from 'rxjs';
import { ProfileParserService } from '../../profile-parser/profile-parser.service';

@AddinExecute({
  name: 'CallcardOpener',
  description: 'Provide a tool and event to open a CallCard'
})
@Injectable()
export class CallcardOpenerService extends Execute {
  private readonly _subs: Subscription[] = [];

  /**
   * An open card tool
   */
  private openCardTool: Tool;

  private _view: Type<any> = null;

  constructor(
    private readonly _currentCardService: CurrentCardService,
    private readonly _navService: NavService,
    private readonly _addinService: AddinService,
    private readonly _profileService: ProfileService,
    private readonly _toolsProviderService: ToolsProviderService,
    private readonly _translator: TranslateService,
    private readonly _profileParser: ProfileParserService,
    private readonly _viewLoader: ViewLoaderService
  ) {
    super();
  }

  public destroy(): void {
    this.disable();
  }

  public execute(): void {
    this._profileService.ready
      .pipe(
        filter(isReady => isReady),
        first()
      )
      .subscribe(() => this.setup());
  }

  private setup() {
    this._view = this._profileParser.getCardView() || this._addinService.defaultView || null;
    if (this._view !== null && this._subs.length === 0) {
      this.createTool();
      this._subs.push(this._currentCardService.$change.subscribe(callcard => this.openCard(callcard)));
      this._subs.push(this._toolsProviderService.register(toolsProvider => this.onTool(toolsProvider)));
    } else {
      this.disable();
    }
  }

  private createTool() {
    this.openCardTool = Tool.createTool({
      text: this._translator.instant('Mobile.OpenCard'),
      icon: 'open',
      priority: 100,
      execute: context => this.openCard(context[0])
    });
  }

  /**
   * It is called when someone needs to open a card
   */
  private openCard(callcard: CallCard): Promise<void> {
    return callcard === null
      ? Promise.resolve()
      : this._navService.getViewOpen(this._view).then(cvi => {
          const nav: IonNav = this._navService.get();
          const page = 0;

          // change this to load profile
          if (cvi) {
            cvi.load(callcard, page);
            return Promise.resolve();
          } else if (this._view !== null) {
            this._viewLoader.show(this._view, { callcard, page });
          }
        });
  }

  private disable() {
    while (this._subs.length) {
      this._subs.pop().unsubscribe();
    }
  }

  /**
   * This is called when someone needs tools from a context
   */
  private onTool(provider: ToolsProvider): void {
    if (provider.context.length === 1 && provider.contextObjectsAre(CallCard)) {
      provider.addTool(this.openCardTool);
    }
  }
}
